import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { can, PLANNING_BOUNDS } from '@seller/shared';
import {
  api,
  ApiError,
  fetchLeadTimes,
  NO_STATUS,
  queryString,
  type BookingPairLever,
  type BookingView,
  type BudgetStatus,
  type BudgetSummary,
  type Confidence,
  type DemandBasis,
  type FamilyPlanRow,
  type LeadTimeCounts,
  type LeadTimeStats,
  type PlanListResult,
  type PlanMeta,
  type PlanRow,
  type PlanSummary,
  type PlanningSettings,
  type PurchasePlanResult,
  type SkuLeadTime,
  type UnmatchedBookingLine,
} from '../api/client.js';
import { useSession } from '../auth/useSession.js';
import { DataTable } from '../components/DataTable.js';
import { ProductThumb } from '../components/ProductThumb.js';
import { ProductPageLink } from '../components/ProductPageLink.js';
import { AnalysisBanner } from '../components/AnalysisBanner.js';
import {
  Alert,
  Button,
  Card,
  Input,
  PageHeader,
  Select,
  Spinner,
  StockNote,
  Tabs,
} from '../components/ui.js';
import { day, decimal, integer, money } from '../format.js';
import { familyPath } from '../lib/families.js';
import { parseCount, parseMoney, parseSettingInt, type ParsedField } from '../lib/parse-input.js';
import {
  BUDGET_STATUS_CLASS,
  BUDGET_STATUS_LABEL,
  CONFIDENCE_CLASS,
  CONFIDENCE_LABEL,
  VELOCITY_SOURCE_FULL,
  VELOCITY_SOURCE_LABEL,
} from '../lib/purchase-plan.js';
import {
  compareInteger,
  compareIsoDate,
  compareNumericString,
  compareText,
  sortingFnFor,
} from '../lib/table-sort.js';

/**
 * The purchase plan: what to buy and how many, per product and per SKU. The
 * whole plan comes down at once; the page sorts, filters, and pages on the
 * client, and the CSV links hand the same rows to a spreadsheet or the
 * purchasing agent. An importer edits the settings, the per-SKU cost, case
 * pack, quantity override (with a note), and the "do not reorder" flag inline;
 * every change recomputes the plan. See docs/purchase-plan.md.
 */

function abbr(label: string, full: string): ReactNode {
  return <span title={full}>{label}</span>;
}

const WORST_CONFIDENCE_ORDER: Record<Confidence, number> = { low: 0, medium: 1, high: 2 };

/** Short labels for a booking shipment window. */
const WINDOW_LABELS: Record<string, string> = {
  ship_1: '1st ship',
  ship_2: '2nd ship',
  ship_3: '3rd ship',
  q2: 'Q2',
  q3: 'Q3',
  q4: 'Q4',
};

/** Invalidates every query the plan feeds after an inline edit. */
function usePlanInvalidate() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['purchase-plan'] }),
      queryClient.invalidateQueries({ queryKey: ['sku'] }),
      queryClient.invalidateQueries({ queryKey: ['families'] }),
      queryClient.invalidateQueries({ queryKey: ['restock'] }),
    ]);
}

export function PurchasePlanPage() {
  const navigate = useNavigate();
  const session = useSession();
  const queryClient = useQueryClient();
  const canEdit = session.status === 'signedIn' && can(session.user.role, 'data:import');

  const [scope, setScope] = useState<'sku' | 'family'>('sku');
  // The plan being viewed. Null until the plan list resolves the current plan;
  // switching plans sets this and persists it as the current plan.
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [status, setStatus] = useState('');
  const [confidence, setConfidence] = useState('');
  const [search, setSearch] = useState('');
  const [onlyToOrder, setOnlyToOrder] = useState(true);
  const [missingCost, setMissingCost] = useState(false);
  // Hide zero-stock duplicates on a shared child ASIN (default on). The server
  // computes and hides them, so this rides in the fetch and every CSV link.
  const [hideDuplicates, setHideDuplicates] = useState(true);
  const [budgetStatus, setBudgetStatus] = useState('');
  const [includeLowConfidence, setIncludeLowConfidence] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // With a booking active, show only the SKUs it leaves out (a review view).
  const [showNotInBooking, setShowNotInBooking] = useState(false);
  // With a booking active, show only the pack-priced lines (packQty > 1), which
  // the owner must check because the plan writes SKU units into a pack cell.
  const [packPricedOnly, setPackPricedOnly] = useState(false);
  // SKUs the owner has zeroed with an override THIS session. Kept visible under
  // "to order only" so they can be un-zeroed without a reload — a client-only
  // display rule that never reaches a download (the CSVs use finalQty > 0).
  const [zeroedThisSession, setZeroedThisSession] = useState<ReadonlySet<number>>(() => new Set());
  const noteOverride = useCallback((id: number, qty: number | string | null) => {
    setZeroedThisSession((prev) => {
      const shouldKeep = qty === 0;
      if (shouldKeep === prev.has(id)) return prev;
      const next = new Set(prev);
      if (shouldKeep) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  // The plan selector's list (pending first, then sent). Its currentPlanId
  // seeds the view before the plan itself loads.
  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () => api.get<PlanListResult>('/api/plans'),
  });
  // Adopt the server's current plan the first time the list arrives.
  useEffect(() => {
    if (selectedPlanId === null && plans.data?.currentPlanId != null) {
      setSelectedPlanId(plans.data.currentPlanId);
    }
  }, [plans.data, selectedPlanId]);

  // The `plan` param is only added once a plan is selected; before that the
  // server serves the current plan and the page adopts its id.
  const planParam = selectedPlanId !== null ? `&plan=${selectedPlanId}` : '';
  const plan = useQuery({
    queryKey: ['purchase-plan', 'sku', hideDuplicates, selectedPlanId],
    queryFn: () =>
      api.get<PurchasePlanResult<PlanRow>>(
        `/api/purchase-plan?scope=sku&hideDuplicates=${hideDuplicates}${planParam}`,
      ),
  });
  const familyPlan = useQuery({
    queryKey: ['purchase-plan', 'family', hideDuplicates, selectedPlanId],
    queryFn: () =>
      api.get<PurchasePlanResult<FamilyPlanRow>>(
        `/api/purchase-plan?scope=family&hideDuplicates=${hideDuplicates}${planParam}`,
      ),
    enabled: scope === 'family',
  });

  // Observed lead times: the overall figure for the settings section and the
  // per-SKU breakdown for the "Obs. lead" column and the inbound cross-check.
  const leadTimes = useQuery({
    queryKey: ['lead-times'],
    queryFn: () => fetchLeadTimes(),
  });
  const leadBySku = useMemo(() => {
    const map = new Map<string, SkuLeadTime>();
    for (const row of leadTimes.data?.bySku ?? []) map.set(row.sellerSku, row);
    return map;
  }, [leadTimes.data]);

  // The plan the server actually built (adopt its id when we had none), and
  // whether it is sent (read-only).
  const activePlan = plan.data?.plan ?? null;
  useEffect(() => {
    if (selectedPlanId === null && activePlan) setSelectedPlanId(activePlan.id);
  }, [activePlan, selectedPlanId]);
  // If the selected plan is gone (it was just deleted), stop pointing the query
  // at it: fall back to the server's current plan, else clear the selection so
  // the chooser shows rather than a 404 for the missing id.
  useEffect(() => {
    const list = plans.data?.plans;
    if (!list || selectedPlanId === null) return;
    if (!list.some((p) => p.id === selectedPlanId)) {
      setSelectedPlanId(plans.data?.currentPlanId ?? null);
    }
  }, [plans.data, selectedPlanId]);
  const isSent = activePlan?.status === 'sent';
  // Whether any plan exists at all. With none (a fresh install, or after the last
  // one is deleted) the page shows a chooser instead of the ephemeral default.
  const plansLoaded = plans.data !== undefined;
  const hasPlans = (plans.data?.plans.length ?? 0) > 0;
  const currentPlanId = plans.data?.currentPlanId ?? null;
  // Show the chooser when there is nothing to open: no plans at all, or plans
  // exist but none is current and none is selected (so the page would otherwise
  // serve the ephemeral default while the selector showed the wrong plan). When
  // a current plan exists it is adopted below, so no chooser flashes then.
  const showChooser =
    plansLoaded && (!hasPlans || (selectedPlanId === null && currentPlanId === null));

  // Switch the viewed plan. Pointing the shared current plan takes the planning
  // permission, so only an editor persists the choice; a viewer just changes
  // what they are looking at (via the plan query param) without moving everyone's
  // current plan. `persist` is set for a deliberate switch (the dropdown, or after
  // create/duplicate/delete), not for merely adopting the server's current plan.
  const selectPlan = useCallback(
    async (id: number, persist = true) => {
      setSelectedPlanId(id);
      if (!persist || !canEdit) return;
      try {
        await api.post('/api/plans/current', { planId: id });
      } catch {
        // A failed persist still switches the view; the choice just is not saved.
      }
      await queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
    [queryClient, canEdit],
  );

  // Create the first plan from the chooser (shown when no plans exist yet).
  const createFirstPlan = useMutation({
    mutationFn: (name: string) => api.post<{ plan: PlanSummary }>('/api/plans', { name }),
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({ queryKey: ['plans'] });
      await selectPlan(res.plan.id);
    },
  });
  const [firstPlanName, setFirstPlanName] = useState('Default plan');

  const statusOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of plan.data?.rows ?? []) {
      const key = row.status ?? NO_STATUS;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()].sort(([a], [b]) => {
      if (a === NO_STATUS) return 1;
      if (b === NO_STATUS) return -1;
      return a.localeCompare(b);
    });
  }, [plan.data]);

  const query = search.trim().toLowerCase();

  const skuRows = useMemo(() => {
    return (
      (plan.data?.rows ?? [])
        // The booking's orderable set: show only the excluded SKUs when reviewing
        // them, otherwise hide them (the plan focuses on what the booking offers).
        .filter((r) => (showNotInBooking ? r.notInBooking : !r.notInBooking))
        .filter((r) =>
          !status ? true : status === NO_STATUS ? r.status === null : r.status === status,
        )
        .filter((r) => (!confidence ? true : r.confidence === confidence))
        // "To order only" keeps rows to order, plus any the owner has zeroed with
        // an override this session, so they can still be cleared without a reload.
        .filter((r) => (!onlyToOrder ? true : r.finalQty > 0 || zeroedThisSession.has(r.id)))
        .filter((r) => (!missingCost ? true : r.finalQty > 0 && r.unitCost === null))
        .filter((r) => (!budgetStatus ? true : r.budget?.status === budgetStatus))
        .filter((r) => (!packPricedOnly ? true : (r.booking?.packQty ?? 1) > 1))
        .filter((r) => {
          if (!query) return true;
          const hay =
            `${r.sellerSku} ${r.asin ?? ''} ${r.title ?? ''} ${r.family?.name ?? ''}`.toLowerCase();
          return hay.includes(query);
        })
    );
  }, [
    plan.data,
    status,
    confidence,
    onlyToOrder,
    missingCost,
    budgetStatus,
    query,
    zeroedThisSession,
    showNotInBooking,
    packPricedOnly,
  ]);

  const familyRows = useMemo(() => {
    return (familyPlan.data?.rows ?? [])
      .filter((r) => (!confidence ? true : r.worstConfidence === confidence))
      .filter((r) => (!onlyToOrder ? true : r.childrenToOrder > 0))
      .filter((r) => (!missingCost ? true : r.uncostedUnits > 0))
      .filter((r) =>
        !budgetStatus ? true : r.budgetStatusCounts[budgetStatus as BudgetStatus] > 0,
      )
      .filter((r) => (!query ? true : `${r.key} ${r.name}`.toLowerCase().includes(query)));
  }, [familyPlan.data, confidence, onlyToOrder, missingCost, budgetStatus, query]);

  const hasBudget = plan.data?.budget != null;
  // The verdict filter only makes sense with a budget; clearing the budget
  // would otherwise leave an invisible filter emptying both tabs.
  useEffect(() => {
    if (plan.data && !hasBudget) setBudgetStatus('');
  }, [plan.data, hasBudget]);

  const totals = useMemo(() => {
    const rows = scope === 'family' ? familyRows : skuRows;
    let units = 0;
    let orderUnits = 0;
    let costCents = 0n;
    let fundedCents = 0n;
    let hasCost = false;
    let costedUnits = 0;
    let uncostedUnits = 0;
    // Net profit of what is ordered: total MAP minus total purchase amount.
    let orderMapCents = 0n;
    let hasMap = false;
    // Net profit is paired: MAP and cost summed only over lines that have BOTH,
    // so the total never mixes a MAP subset with a fuller cost sum. `mapMissing`
    // counts funded lines with no MAP, so the figure can be marked partial.
    let profitMapCents = 0n;
    let profitCostCents = 0n;
    let hasProfit = false;
    let mapMissing = 0;
    const cents = (cost: string | null): bigint | null => {
      if (cost === null) return null;
      const m = /^(-)?(\d+)(?:\.(\d+))?$/.exec(cost);
      if (!m) return null;
      const value =
        BigInt(m[2] as string) * 100n + BigInt((m[3] ?? '').padEnd(2, '0').slice(0, 2) || '0');
      return m[1] === '-' ? -value : value;
    };
    for (const r of rows) {
      units += r.finalQty;
      const ext = cents(r.extendedCost);
      if (ext !== null) {
        hasCost = true;
        costCents += ext;
      }
      // Total MAP is over every line that has one (the display figure).
      const om = cents((r as { orderMap: string | null }).orderMap);
      if (om !== null) {
        hasMap = true;
        orderMapCents += om;
      }
      if (scope === 'family') {
        const row = r as FamilyPlanRow;
        costedUnits += row.costedUnits;
        uncostedUnits += row.uncostedUnits;
        // Funded units exclude uncosted lines (which keep their quantity but
        // were never allocated), so the roll-up's orderQty is narrowed here.
        orderUnits += row.orderQty - row.uncostedOrderUnits;
        fundedCents += cents(row.budgetCost) ?? 0n;
        // The API already paired the family's orderMap/orderCost over children
        // with both; add them, and carry the family's missing count.
        const fm = cents(row.orderMap);
        const fc = cents(row.orderCost);
        if (fm !== null && fc !== null) {
          hasProfit = true;
          profitMapCents += fm;
          profitCostCents += fc;
        }
        mapMissing += row.orderMapMissing;
      } else {
        const row = r as PlanRow;
        if (row.finalQty > 0) {
          if (row.unitCost !== null) costedUnits += row.finalQty;
          else uncostedUnits += row.finalQty;
        }
        if (row.budget && row.budget.status !== 'uncosted') orderUnits += row.budget.qty;
        fundedCents += cents(row.budget?.cost ?? null) ?? 0n;
        if (row.orderQty > 0) {
          const pm = cents(row.orderMap);
          const pc = cents(row.orderCost);
          if (pm !== null && pc !== null) {
            hasProfit = true;
            profitMapCents += pm;
            profitCostCents += pc;
          } else if (row.orderMap === null) {
            mapMissing += 1;
          }
        }
      }
    }
    const dollars = (c: bigint) =>
      `${(c / 100n).toString()}.${(c % 100n).toString().padStart(2, '0')}`;
    return {
      count: rows.length,
      units,
      orderUnits,
      cost: hasCost ? dollars(costCents) : null,
      fundedCost: dollars(fundedCents),
      costedUnits,
      uncostedUnits,
      orderMap: hasMap ? dollars(orderMapCents) : null,
      netProfit: hasProfit ? dollars(profitMapCents - profitCostCents) : null,
      netProfitMissing: mapMissing,
    };
  }, [scope, skuRows, familyRows]);

  const planQ = selectedPlanId !== null ? String(selectedPlanId) : '';
  const csvQuery = queryString({
    plan: planQ,
    scope,
    status: scope === 'family' ? '' : status,
    confidence,
    q: search.trim(),
    onlyToOrder: onlyToOrder ? 'true' : '',
    missingCost: missingCost ? 'true' : '',
    budgetStatus,
    // Default is on server-side, so only send the override.
    hideDuplicates: hideDuplicates ? '' : 'false',
    notInBooking: showNotInBooking ? 'true' : '',
  });
  const agentQuery = queryString({
    plan: planQ,
    scope,
    status: scope === 'family' ? '' : status,
    confidence,
    q: search.trim(),
    onlyToOrder: onlyToOrder ? 'true' : '',
    missingCost: missingCost ? 'true' : '',
    budgetStatus,
    includeLowConfidence: includeLowConfidence ? 'true' : '',
    hideDuplicates: hideDuplicates ? '' : 'false',
  });
  // Low-confidence rows the agent CSV would leave out (unless included or given
  // an override): a count for the notice by the download button.
  const lowLeftOut = useMemo(
    () =>
      (plan.data?.rows ?? []).filter(
        (r) => r.finalQty > 0 && r.confidence === 'low' && r.overrideQty === null,
      ).length,
    [plan.data],
  );

  const settings = plan.data?.settings;
  const seasonal = settings?.demandBasis === 'seasonal';
  const activeBooking = plan.data?.activeBooking ?? null;
  const bookingActive = activeBooking !== null;
  // The filled-workbook download uses the same filters and the same
  // include-low-confidence choice as the agent CSV, plus the not-in-booking view.
  const bookingQuery = queryString({
    plan: planQ,
    scope,
    status: scope === 'family' ? '' : status,
    confidence,
    q: search.trim(),
    onlyToOrder: onlyToOrder ? 'true' : '',
    missingCost: missingCost ? 'true' : '',
    budgetStatus,
    includeLowConfidence: includeLowConfidence ? 'true' : '',
    hideDuplicates: hideDuplicates ? '' : 'false',
  });

  // The columns depend only on stable values; each editable cell owns its own
  // save mutation, so nothing here remounts a cell mid-edit.
  const skuColumns = useMemo(
    () =>
      buildSkuColumns(
        {
          canEdit,
          seasonal,
          hasBudget,
          bookingActive,
          activeBookingId: activeBooking?.id ?? null,
          planId: selectedPlanId,
          readOnly: isSent,
          leadBySku,
        },
        (id) => void navigate(`/skus/${id}`),
        noteOverride,
      ),
    [
      canEdit,
      seasonal,
      hasBudget,
      bookingActive,
      activeBooking?.id,
      selectedPlanId,
      isSent,
      navigate,
      noteOverride,
      leadBySku,
    ],
  );
  const familyColumns = useMemo(
    () => buildFamilyColumns({ hasBudget, seasonal }),
    [hasBudget, seasonal],
  );

  const filterProps = {
    status,
    setStatus,
    statusOptions,
    confidence,
    setConfidence,
    search,
    setSearch,
    onlyToOrder,
    setOnlyToOrder,
    missingCost,
    setMissingCost,
    hideDuplicates,
    setHideDuplicates,
    hiddenCount: plan.data?.hiddenCount ?? 0,
    budgetStatus,
    setBudgetStatus,
    hasBudget,
    includeLowConfidence,
    setIncludeLowConfidence,
    lowLeftOut,
    csvQuery,
    agentQuery,
    bookingActive,
    bookingQuery,
    packPricedOnly,
    setPackPricedOnly,
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Purchase plan"
        description="What to buy and how many, per product and per SKU — on the recent rate over lead time plus cover, or on last year's same window through a horizon date — and, with a budget, which lines it funds. See docs/purchase-plan.md."
      />

      <AnalysisBanner />

      {plan.isPending && <Spinner label="Building the plan" />}
      {plan.isError && <Alert>{plan.error.message}</Alert>}

      {/* Nothing to open (no plans, or none current/selected): a chooser, not
          the ephemeral default plan's table. */}
      {showChooser && (
        <Card className="space-y-3 p-6">
          <div>
            <h2 className="text-base font-medium text-strong">
              {hasPlans ? 'Choose a purchase plan' : 'No purchase plans yet'}
            </h2>
            <p className="text-sm text-muted">
              {hasPlans
                ? 'Pick a plan to view, or create a new one. You can keep several plans and mark one sent when its order is placed.'
                : 'Create a plan to start choosing what to order. You can keep several plans and mark one sent when its order is placed.'}
            </p>
          </div>
          {hasPlans && (
            <label className="block">
              <span className="mb-1 block text-xs text-muted">Open a plan</span>
              <Select
                aria-label="Choose a purchase plan"
                value=""
                onChange={(e) => {
                  if (e.target.value) void selectPlan(Number(e.target.value));
                }}
                className="w-72"
              >
                <option value="">Select a plan…</option>
                {(plans.data?.plans ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.status === 'sent' ? `🔒 ${p.name}` : p.name}
                  </option>
                ))}
              </Select>
            </label>
          )}
          {canEdit ? (
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (firstPlanName.trim()) createFirstPlan.mutate(firstPlanName.trim());
              }}
            >
              <label className="block">
                <span className="mb-1 block text-xs text-muted">Plan name</span>
                <Input
                  value={firstPlanName}
                  onChange={(e) => setFirstPlanName(e.target.value)}
                  className="w-72"
                />
              </label>
              <Button type="submit" disabled={createFirstPlan.isPending || !firstPlanName.trim()}>
                New plan
              </Button>
            </form>
          ) : (
            <p className="text-sm text-muted">Ask an editor to create the first plan.</p>
          )}
          {createFirstPlan.isError && (
            <p className="text-xs text-danger-text" role="alert">
              {createFirstPlan.error instanceof ApiError
                ? createFirstPlan.error.message
                : 'Could not create the plan.'}
            </p>
          )}
        </Card>
      )}

      {hasPlans && selectedPlanId !== null && plan.data && settings && (
        <>
          <PlanBar
            plans={plans.data?.plans ?? []}
            selectedPlanId={selectedPlanId}
            activePlan={activePlan}
            canEdit={canEdit}
            onSelect={(id) => void selectPlan(id)}
            totals={totals}
            sendPayload={{
              scope,
              includeLowConfidence,
              hideDuplicates,
              filter: {
                status: scope === 'family' ? undefined : status || undefined,
                q: search.trim() || undefined,
                confidence: (confidence || undefined) as 'high' | 'medium' | 'low' | undefined,
                onlyToOrder: onlyToOrder || undefined,
                missingCost: missingCost || undefined,
                budgetStatus: (budgetStatus || undefined) as BudgetStatus | undefined,
                notInBooking: showNotInBooking || undefined,
              },
            }}
          />

          <SettingsBar
            settings={settings}
            today={plan.data.today}
            canEdit={canEdit}
            planId={selectedPlanId}
            activePlan={activePlan}
            open={settingsOpen}
            onToggle={() => setSettingsOpen((v) => !v)}
          />

          <LeadTimesSummary
            overall={leadTimes.data?.overall ?? null}
            counts={leadTimes.data?.counts ?? null}
            loading={leadTimes.isPending}
            settings={settings}
            planId={selectedPlanId}
            canApply={canEdit && !isSent}
          />

          <HorizonNotice settings={settings} rows={plan.data.rows} />
          <ManyUnknownNotice rows={plan.data.rows} />
          {bookingActive && activeBooking && (
            <BookingPanel
              booking={activeBooking}
              notInBookingCount={plan.data.notInBookingCount}
              unmatchedLines={plan.data.unmatchedBookingLines}
              showNotInBooking={showNotInBooking}
              onToggleNotInBooking={() => setShowNotInBooking((v) => !v)}
              bookingQuery={bookingQuery}
            />
          )}
          {plan.data.budget && <BudgetBar summary={plan.data.budget} onFilter={setBudgetStatus} />}

          <Card>
            <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
              <Tabs
                items={[
                  { id: 'sku', label: 'By SKU' },
                  { id: 'family', label: 'By product' },
                ]}
                active={scope}
                onChange={(id) => setScope(id as 'sku' | 'family')}
              />
              <div className="ml-auto flex flex-wrap items-center gap-4 text-xs text-muted">
                <span>
                  {integer(totals.count)} {scope === 'family' ? 'product' : 'SKU'}
                  {totals.count === 1 ? '' : 's'}
                </span>
                <span>
                  {hasBudget ? 'Plan needs:' : 'Units to order:'}{' '}
                  <span className="font-medium text-strong">{integer(totals.units)}</span>
                  {hasBudget && (
                    <>
                      {' · '}Funded:{' '}
                      <span className="font-medium text-strong">{integer(totals.orderUnits)}</span>
                    </>
                  )}
                </span>
                <span>
                  {hasBudget ? 'Plan cost:' : 'Extended cost:'}{' '}
                  <span className="font-medium text-strong">{money(totals.cost)}</span>
                  {totals.uncostedUnits > 0 && (
                    <span className="ml-1 text-warn-text">
                      (partial — {integer(totals.costedUnits)} of{' '}
                      {integer(totals.costedUnits + totals.uncostedUnits)} units costed)
                    </span>
                  )}
                  {hasBudget && (
                    <>
                      {' · '}Funded:{' '}
                      <span className="font-medium text-strong">{money(totals.fundedCost)}</span>
                    </>
                  )}
                </span>
                {bookingActive && totals.netProfit !== null && (
                  <span title="Total MAP of everything ordered (MAP × quantity, honoring MAP-per-pack) minus the total purchase amount (unit cost × quantity). Computed only over lines that have both a MAP and a cost.">
                    Order MAP:{' '}
                    <span className="font-medium text-strong">{money(totals.orderMap)}</span>
                    {' · '}Net profit:{' '}
                    <span className="font-medium text-ok-text">{money(totals.netProfit)}</span>
                    {totals.netProfitMissing > 0 && (
                      <span className="ml-1 text-xs text-muted">
                        (partial — {integer(totals.netProfitMissing)} line
                        {totals.netProfitMissing === 1 ? '' : 's'} without MAP)
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>

            {scope === 'sku' ? (
              <DataTable
                data={skuRows}
                columns={skuColumns}
                getRowId={(r) => String(r.id)}
                initialSorting={[{ id: 'finalQty', desc: true }]}
                storageKey={`purchase-plan${seasonal ? '-seasonal' : ''}${hasBudget ? '-budget' : ''}${bookingActive ? '-booking' : ''}`}
                resetKey={`${status}|${confidence}|${search}|${onlyToOrder}|${missingCost}|${budgetStatus}|${hideDuplicates}|${showNotInBooking}`}
                dense
                stickyFirstColumn
                emptyText="Nothing to order under the current filters."
                toolbar={
                  <Filters
                    {...filterProps}
                    stockSource={plan.data.stock.source}
                    stockAsOf={plan.data.stock.asOf}
                  />
                }
              />
            ) : familyPlan.isPending ? (
              <div className="p-6">
                <Spinner label="Rolling up by product" />
              </div>
            ) : (
              <DataTable
                data={familyRows}
                columns={familyColumns}
                getRowId={(r) => r.key}
                initialSorting={[{ id: 'finalQty', desc: true }]}
                storageKey={`purchase-plan-family${hasBudget ? '-budget' : ''}`}
                resetKey={`${confidence}|${search}|${onlyToOrder}|${missingCost}|${budgetStatus}|${hideDuplicates}`}
                dense
                stickyFirstColumn
                onRowClick={(row) => void navigate(familyPath(row.key))}
                emptyText="No products to order under the current filters."
                toolbar={
                  <Filters
                    {...filterProps}
                    stockSource={plan.data.stock.source}
                    stockAsOf={plan.data.stock.asOf}
                    hideStatus
                  />
                }
              />
            )}
          </Card>
        </>
      )}
    </div>
  );
}

/** The budget's headline: what it funds, what it leaves out, with one-click filters. */
function BudgetBar({
  summary,
  onFilter,
}: {
  summary: BudgetSummary;
  onFilter: (status: string) => void;
}) {
  const overBudget = compareNumericString(summary.planCost, summary.budget) > 0;
  const chip = (status: BudgetStatus, count: number) =>
    count > 0 && (
      <button
        type="button"
        onClick={() => onFilter(status)}
        className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${BUDGET_STATUS_CLASS[status]}`}
        title={`Show only ${BUDGET_STATUS_LABEL[status].toLowerCase()} lines`}
      >
        {integer(count)} {BUDGET_STATUS_LABEL[status].toLowerCase()}
      </button>
    );
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <span className="text-muted">
          Budget <span className="font-medium text-strong">{money(summary.budget)}</span>
        </span>
        <span className="text-muted">
          Allocated <span className="font-medium text-strong">{money(summary.allocated)}</span>
        </span>
        <span className="text-muted">
          Remaining <span className="font-medium text-strong">{money(summary.remaining)}</span>
        </span>
        <span className="text-muted">
          Plan needs{' '}
          <span className={`font-medium ${overBudget ? 'text-danger-text' : 'text-strong'}`}>
            {money(summary.planCost)}
          </span>
          {summary.uncostedUnits > 0 && (
            <span className="ml-1 text-warn-text">
              + {integer(summary.uncostedUnits)} uncosted unit
              {summary.uncostedUnits === 1 ? '' : 's'}
            </span>
          )}
        </span>
        <span className="flex flex-wrap items-center gap-1.5">
          {chip('funded', summary.funded)}
          {chip('partial', summary.partial)}
          {chip('cut', summary.cut)}
          {chip('held', summary.held)}
          {chip('uncosted', summary.uncosted)}
        </span>
        {summary.unfundedUnits > 0 && (
          <span className="text-xs text-muted">
            {integer(summary.unfundedUnits)} unit{summary.unfundedUnits === 1 ? '' : 's'} unfunded.
            Lines are funded in order: your overrides first, then whatever runs out soonest. To
            force a line in, give it an override; to fund more, raise the budget.
          </span>
        )}
        {summary.held > 0 && (
          <span className="text-xs text-muted">
            {integer(summary.held)} low-confidence line{summary.held === 1 ? '' : 's'} (
            {integer(summary.heldUnits)} unit{summary.heldUnits === 1 ? '' : 's'}) held for review:
            the agent list leaves them out, so the budget spends nothing on them. Give one an
            override to fund it, or include them all when downloading for the agent.
          </span>
        )}
      </div>
    </Card>
  );
}

/**
 * The active booking's headline: the booking name, how many SKUs it left out
 * (with a toggle to review them), the filled-workbook download, and the lines the
 * booking offers that the plan cannot size (no listing / unmatched).
 */
function BookingPanel({
  booking,
  notInBookingCount,
  unmatchedLines,
  showNotInBooking,
  onToggleNotInBooking,
  bookingQuery,
}: {
  booking: { id: number; name: string; bookingYear: number };
  notInBookingCount: number;
  unmatchedLines: UnmatchedBookingLine[];
  showNotInBooking: boolean;
  onToggleNotInBooking: () => void;
  bookingQuery: string;
}) {
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <span className="text-muted">
          Plan against{' '}
          <span className="font-medium text-strong">
            {booking.name} ({booking.bookingYear})
          </span>
        </span>
        <span className="text-muted">
          The booking is the orderable set — the plan, budget, and exports focus only on its lines.
        </span>
        {notInBookingCount > 0 && (
          <button
            type="button"
            onClick={onToggleNotInBooking}
            className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ring-line-strong hover:bg-sunken"
          >
            {showNotInBooking
              ? 'Show the booking’s SKUs'
              : `${integer(notInBookingCount)} SKU${notInBookingCount === 1 ? '' : 's'} not in the booking`}
          </button>
        )}
        <a
          href={`/api/purchase-plan-booking.xlsx${bookingQuery}`}
          className="ml-auto inline-flex items-center rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-brand-on hover:bg-brand-700"
        >
          Download filled workbook
        </a>
      </div>
      {unmatchedLines.length > 0 && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer text-muted">
            {integer(unmatchedLines.length)} orderable line
            {unmatchedLines.length === 1 ? '' : 's'} in the booking with no listing or unmatched —
            the plan cannot size {unmatchedLines.length === 1 ? 'it' : 'them'}.
          </summary>
          <ul className="mt-2 space-y-1">
            {unmatchedLines.map((l) => (
              <li key={l.lineId} className="text-xs text-muted">
                <span className="font-mono">{l.itemCode}</span>{' '}
                <span className="text-body">{l.model}</span>
                {l.color ? ` · ${l.color}` : ''}
                {l.sizeLabel ? ` · ${l.sizeLabel}` : ''}
                {' · '}
                {WINDOW_LABELS[l.window] ?? l.window}
                {l.prebook ? ' · pre-book' : ''}
                {' · '}
                {l.reason ?? (l.matchState === 'no_listing' ? 'no listing' : 'unmatched')}
                {l.unitCost ? ` · cost ${money(l.unitCost)}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}

/**
 * The observed lead times beside the setting: the median and p80 of the total
 * lead time across every sent plan line, against the current lead-time setting,
 * with a button that applies the p80 suggestion to the setting after a
 * confirmation. The suggestion is never applied automatically. See
 * docs/lead-times.md.
 */
function LeadTimesSummary({
  overall,
  counts,
  loading,
  settings,
  planId,
  canApply,
}: {
  overall: LeadTimeStats | null;
  counts: LeadTimeCounts | null;
  loading: boolean;
  settings: PlanningSettings;
  planId: number | null;
  canApply: boolean;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const apply = useMutation({
    mutationFn: (leadTimeDays: number) => {
      const body: PlanningSettings = { ...settings, leadTimeDays };
      return planId !== null
        ? api.patch(`/api/plans/${planId}`, { settings: body })
        : api.put<PlanningSettings>('/api/planning/settings', body);
    },
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['purchase-plan'] }),
        queryClient.invalidateQueries({ queryKey: ['plans'] }),
      ]);
    },
    onError: (caught: unknown) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not apply the lead time.'),
  });

  const suggestion = overall?.suggestedLeadTimeDays ?? null;
  return (
    <Card className="p-4" data-testid="lead-times-summary">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-body">
          <span className="font-medium text-strong">Lead times</span>{' '}
          {loading ? (
            <span className="text-muted">loading…</span>
          ) : overall && overall.n > 0 ? (
            <span className="text-muted">
              observed median{' '}
              <span className="font-medium text-strong">{integer(overall.medianTotal)}d</span> · p80{' '}
              <span className="font-medium text-strong">{integer(overall.p80Total)}d</span> over{' '}
              {integer(overall.n)} sent {overall.n === 1 ? 'order' : 'orders'}. Current setting:{' '}
              <span className="font-medium text-strong">{integer(settings.leadTimeDays)}d</span>.
              {counts && (
                <span className="text-muted">
                  {' '}
                  Coverage: {integer(counts.lines)} plan {counts.lines === 1 ? 'line' : 'lines'}{' '}
                  across {integer(counts.plans)} {counts.plans === 1 ? 'plan' : 'plans'},{' '}
                  {integer(counts.shipments)} {counts.shipments === 1 ? 'shipment' : 'shipments'}
                  {counts.earliestPlanCreatedAt
                    ? `; inbound history from ${day(counts.earliestPlanCreatedAt)}`
                    : ''}
                  .
                </span>
              )}
            </span>
          ) : (
            <span className="text-muted">
              no measured lead times yet — they appear once a sent plan matches an inbound shipment
              and a ledger receipt.
              {counts?.earliestPlanCreatedAt
                ? ` Inbound history fetched from ${day(counts.earliestPlanCreatedAt)}.`
                : ''}
            </span>
          )}
        </div>
        {canApply && suggestion !== null && suggestion !== settings.leadTimeDays && (
          <Button
            type="button"
            variant="secondary"
            disabled={apply.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Set this plan's lead time to the observed p80 of ${suggestion} days? It is currently ${settings.leadTimeDays} days.`,
                )
              ) {
                apply.mutate(suggestion);
              }
            }}
          >
            {apply.isPending ? 'Applying…' : `Apply ${suggestion}d`}
          </Button>
        )}
      </div>
      {error && (
        <div className="mt-2">
          <Alert>{error}</Alert>
        </div>
      )}
    </Card>
  );
}

/** Warns when a seasonal horizon has passed (every seasonal demand is then zero). */
function HorizonNotice({ settings, rows }: { settings: PlanningSettings; rows: PlanRow[] }) {
  if (settings.demandBasis !== 'seasonal') return null;
  const first = rows.find((r) => r.seasonal !== null);
  if (!first?.seasonal || first.seasonal.horizonDays > 0) return null;
  return (
    <Alert tone="warning">
      The horizon date ({day(settings.horizonDate)}) has passed, so every seasonal demand is zero.
      Set a new horizon in the planning settings.
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Toolbar and filters
// ---------------------------------------------------------------------------

function Filters(props: {
  status: string;
  setStatus: (v: string) => void;
  statusOptions: [string, number][];
  confidence: string;
  setConfidence: (v: string) => void;
  search: string;
  setSearch: (v: string) => void;
  onlyToOrder: boolean;
  setOnlyToOrder: (v: boolean) => void;
  missingCost: boolean;
  setMissingCost: (v: boolean) => void;
  hideDuplicates: boolean;
  setHideDuplicates: (v: boolean) => void;
  hiddenCount: number;
  budgetStatus: string;
  setBudgetStatus: (v: string) => void;
  hasBudget: boolean;
  includeLowConfidence: boolean;
  setIncludeLowConfidence: (v: boolean) => void;
  lowLeftOut: number;
  stockSource: 'fba_inventory' | 'snapshot' | 'listing';
  stockAsOf: string | null;
  csvQuery: string;
  agentQuery: string;
  bookingActive: boolean;
  bookingQuery: string;
  packPricedOnly: boolean;
  setPackPricedOnly: (v: boolean) => void;
  hideStatus?: boolean;
}) {
  return (
    <>
      <label className="flex items-center gap-1.5 text-xs text-body">
        <input
          type="checkbox"
          checked={props.onlyToOrder}
          onChange={(e) => props.setOnlyToOrder(e.target.checked)}
          className="rounded border-line-strong"
        />
        To order only
      </label>
      <label className="flex items-center gap-1.5 text-xs text-body">
        <input
          type="checkbox"
          checked={props.missingCost}
          onChange={(e) => props.setMissingCost(e.target.checked)}
          className="rounded border-line-strong"
        />
        Missing cost
      </label>
      {props.bookingActive && (
        <label
          className="flex items-center gap-1.5 text-xs text-body"
          title="Show only booking lines priced per pack of more than one unit. The plan writes the SKU’s own unit count into a pack-priced cell, so these lines must be checked by hand."
        >
          <input
            type="checkbox"
            checked={props.packPricedOnly}
            onChange={(e) => props.setPackPricedOnly(e.target.checked)}
            className="rounded border-line-strong"
          />
          Pack-priced only
        </label>
      )}
      <label
        className="flex items-center gap-1.5 text-xs text-body"
        title="Among SKUs on one child ASIN, hide the zero-stock ones when a sibling has on-hand, inbound, or reserved stock."
      >
        <input
          type="checkbox"
          checked={props.hideDuplicates}
          onChange={(e) => props.setHideDuplicates(e.target.checked)}
          className="rounded border-line-strong"
        />
        Hide zero-stock duplicates
        {props.hiddenCount > 0 && (
          <span className="text-muted">({integer(props.hiddenCount)} hidden)</span>
        )}
      </label>
      {props.hasBudget && (
        <div className="w-36">
          <Select
            value={props.budgetStatus}
            onChange={(e) => props.setBudgetStatus(e.target.value)}
            aria-label="Budget verdict"
          >
            <option value="">Any verdict</option>
            <option value="funded">Funded</option>
            <option value="partial">Partial</option>
            <option value="cut">Cut</option>
            <option value="held">Review</option>
            <option value="uncosted">No cost</option>
          </Select>
        </div>
      )}
      {!props.hideStatus && (
        <div className="w-40">
          <Select
            value={props.status}
            onChange={(e) => props.setStatus(e.target.value)}
            aria-label="Status"
          >
            <option value="">Any status</option>
            {props.statusOptions.map(([s, count]) => (
              <option key={s} value={s}>
                {s === NO_STATUS ? '(no status)' : s} ({integer(count)})
              </option>
            ))}
          </Select>
        </div>
      )}
      <div className="w-36">
        <Select
          value={props.confidence}
          onChange={(e) => props.setConfidence(e.target.value)}
          aria-label="Confidence"
        >
          <option value="">Any confidence</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </Select>
      </div>
      <div className="w-44">
        <Input
          value={props.search}
          onChange={(e) => props.setSearch(e.target.value)}
          placeholder="Search SKU, ASIN, title"
          aria-label="Search"
        />
      </div>
      <StockNote source={props.stockSource} asOf={props.stockAsOf} />
      <a
        href={`/api/purchase-plan.csv${props.csvQuery}`}
        className="inline-flex items-center rounded-md bg-surface px-3 py-2 text-sm font-medium text-body ring-1 ring-inset ring-line-strong hover:bg-sunken"
      >
        Download CSV
      </a>
      <a
        href={`/api/purchase-plan-agent.csv${props.agentQuery}`}
        className="inline-flex items-center rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-brand-on hover:bg-brand-700"
      >
        Download for agent
      </a>
      {props.bookingActive && (
        <a
          href={`/api/purchase-plan-booking.xlsx${props.bookingQuery}`}
          className="inline-flex items-center rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-brand-on hover:bg-brand-700"
          title="A copy of the supplier's booking workbook with the funded quantities written into the grid."
        >
          Download filled workbook
        </a>
      )}
      {props.lowLeftOut > 0 && !props.includeLowConfidence && (
        <span className="flex items-center gap-1.5 text-xs text-warn-text">
          {integer(props.lowLeftOut)} low-confidence row{props.lowLeftOut === 1 ? '' : 's'} left out
          —
          <button type="button" className="underline" onClick={() => props.setConfidence('low')}>
            review
          </button>
          or
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={props.includeLowConfidence}
              onChange={(e) => props.setIncludeLowConfidence(e.target.checked)}
              className="rounded border-line-strong"
            />
            include them
          </label>
        </span>
      )}
      {props.includeLowConfidence && (
        <label className="flex items-center gap-1 text-xs text-warn-text">
          <input
            type="checkbox"
            checked={props.includeLowConfidence}
            onChange={(e) => props.setIncludeLowConfidence(e.target.checked)}
            className="rounded border-line-strong"
          />
          Low-confidence rows included
        </label>
      )}
    </>
  );
}

/** A notice when on-hand is unknown or estimated for many SKUs. */
function ManyUnknownNotice({ rows }: { rows: PlanRow[] }) {
  const unknown = rows.filter((r) => r.stockUnknown).length;
  if (rows.length === 0 || unknown < Math.max(5, rows.length * 0.1)) return null;
  return (
    <Alert tone="warning">
      On-hand stock is unknown for {integer(unknown)} of {integer(rows.length)} SKUs, so the plan
      leans on sales history for them and counts their stock as zero. Upload a Manage FBA Inventory
      file for exact on-hand.
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Plan bar (the selector and the plan actions)
// ---------------------------------------------------------------------------

interface SendPayload {
  scope: 'sku' | 'family';
  includeLowConfidence: boolean;
  hideDuplicates: boolean;
  filter: {
    status?: string | undefined;
    q?: string | undefined;
    confidence?: 'high' | 'medium' | 'low' | undefined;
    onlyToOrder?: boolean | undefined;
    missingCost?: boolean | undefined;
    budgetStatus?: BudgetStatus | undefined;
    notInBooking?: boolean | undefined;
  };
}

/**
 * The plan selector and the plan lifecycle actions: choose a plan to view,
 * create a new one, duplicate any plan into a fresh pending one, rename or
 * delete a pending plan, and mark a pending plan sent (which freezes it). A sent
 * plan shows a lock and offers only Duplicate. See docs/purchase-plan.md, "Plans".
 */
function PlanBar({
  plans,
  selectedPlanId,
  activePlan,
  canEdit,
  onSelect,
  totals,
  sendPayload,
}: {
  plans: PlanSummary[];
  selectedPlanId: number | null;
  activePlan: PlanMeta | null;
  canEdit: boolean;
  onSelect: (id: number) => void;
  totals: { units: number; cost: string | null; netProfit: string | null };
  sendPayload: SendPayload;
}) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'new' | 'duplicate' | 'rename' | 'delete' | 'send' | null>(
    null,
  );
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const pending = plans.filter((p) => p.status === 'pending');
  const sent = plans.filter((p) => p.status === 'sent');
  const selected = plans.find((p) => p.id === selectedPlanId) ?? null;
  const isSent = activePlan?.status === 'sent';

  const invalidateAll = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['plans'] }),
      queryClient.invalidateQueries({ queryKey: ['purchase-plan'] }),
    ]);

  function close() {
    setDialog(null);
    setName('');
    setError(null);
  }

  const onError = (caught: unknown) =>
    setError(caught instanceof ApiError ? caught.message : 'Could not save.');

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ plan: PlanSummary; horizonCleared?: boolean }>('/api/plans', body),
    onSuccess: async (res) => {
      await invalidateAll();
      onSelect(res.plan.id);
      close();
      // Duplicating a seasonal plan whose horizon had passed drops the stale
      // horizon; tell the owner so they set a new one on the copy.
      setNotice(
        res.horizonCleared
          ? 'The copied plan is seasonal but its horizon date had already passed, so it was cleared — set a new horizon in the planning settings.'
          : null,
      );
    },
    onError,
  });
  const rename = useMutation({
    mutationFn: () => api.patch(`/api/plans/${selectedPlanId}`, { name: name.trim() }),
    onSuccess: async () => {
      await invalidateAll();
      close();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/plans/${selectedPlanId}`),
    onSuccess: async () => {
      await invalidateAll();
      const next =
        pending.find((p) => p.id !== selectedPlanId) ?? plans.find((p) => p.id !== selectedPlanId);
      if (next) onSelect(next.id);
      close();
    },
    onError,
  });
  const send = useMutation({
    mutationFn: () => api.post(`/api/plans/${selectedPlanId}/send`, sendPayload),
    onSuccess: async () => {
      await invalidateAll();
      close();
    },
    onError,
  });

  const busy = create.isPending || rename.isPending || remove.isPending || send.isPending;

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted">Plan</span>
          <Select
            aria-label="Select a purchase plan"
            value={selectedPlanId === null ? '' : String(selectedPlanId)}
            onChange={(e) => onSelect(Number(e.target.value))}
            className="w-64"
          >
            {plans.length === 0 && <option value="">No plans yet</option>}
            {/* A placeholder while nothing is current, so the browser never shows
                the first option as if it were the selected plan. */}
            {plans.length > 0 && selectedPlanId === null && (
              <option value="">Select a plan…</option>
            )}
            {pending.length > 0 && (
              <optgroup label="Pending">
                {pending.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
            )}
            {sent.length > 0 && (
              <optgroup label="Sent">
                {sent.map((p) => (
                  <option key={p.id} value={p.id}>
                    🔒 {p.name}
                    {p.sentAt ? ` — sent ${day(p.sentAt.slice(0, 10))}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
          </Select>
        </label>
        {isSent && (
          <span className="inline-flex items-center rounded bg-sunken px-2 py-0.5 text-xs text-muted">
            🔒 Sent{selected?.sentByName ? ` by ${selected.sentByName}` : ''} — read-only
          </span>
        )}
        {canEdit && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => setDialog('new')}>
              New plan
            </Button>
            {selectedPlanId !== null && (
              <Button variant="secondary" onClick={() => setDialog('duplicate')}>
                Duplicate
              </Button>
            )}
            {selected?.status === 'pending' && (
              <>
                <Button variant="ghost" onClick={() => setDialog('rename')}>
                  Rename
                </Button>
                <Button variant="ghost" onClick={() => setDialog('delete')}>
                  Delete
                </Button>
                <Button onClick={() => setDialog('send')}>Mark sent</Button>
              </>
            )}
          </div>
        )}
      </div>

      {dialog && (
        <div className="mt-3 border-t border-line pt-3">
          {(dialog === 'new' || dialog === 'duplicate' || dialog === 'rename') && (
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (dialog === 'rename') rename.mutate();
                else if (dialog === 'duplicate')
                  create.mutate({ name: name.trim(), fromPlanId: selectedPlanId });
                else create.mutate({ name: name.trim() });
              }}
            >
              <label className="block">
                <span className="mb-1 block text-xs text-muted">
                  {dialog === 'rename'
                    ? 'New name'
                    : dialog === 'duplicate'
                      ? `Name for the copy of ${selected?.name ?? 'the plan'}`
                      : 'Plan name'}
                </span>
                <Input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Holiday order 2026"
                  className="w-72"
                />
              </label>
              <Button type="submit" disabled={busy || name.trim() === ''}>
                {dialog === 'rename' ? 'Rename' : dialog === 'duplicate' ? 'Duplicate' : 'Create'}
              </Button>
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
            </form>
          )}
          {dialog === 'delete' && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>
                Delete the pending plan{' '}
                <span className="font-medium text-strong">{selected?.name}</span> and its overrides?
                This cannot be undone.
              </span>
              <Button variant="danger" disabled={busy} onClick={() => remove.mutate()}>
                Delete plan
              </Button>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
            </div>
          )}
          {dialog === 'send' && (
            <div className="space-y-2 text-sm">
              <p>
                Mark <span className="font-medium text-strong">{selected?.name}</span> sent? This
                freezes the order below and <span className="font-medium">cannot be undone</span> —
                a sent plan can never be edited or reopened.
              </p>
              <p className="text-muted">
                Freezing: <span className="font-medium text-strong">{integer(totals.units)}</span>{' '}
                units, <span className="font-medium text-strong">{money(totals.cost)}</span>
                {totals.netProfit !== null && (
                  <>
                    {' · '}net profit{' '}
                    <span className="font-medium text-ok-text">{money(totals.netProfit)}</span>
                  </>
                )}{' '}
                (the current filters and include-low-confidence choice).
              </p>
              <div className="flex gap-2">
                <Button disabled={busy} onClick={() => send.mutate()}>
                  {send.isPending ? 'Sending…' : 'Mark sent'}
                </Button>
                <Button variant="ghost" onClick={close}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {error && (
            <p className="mt-2 text-xs text-danger-text" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
      {notice && (
        <div className="mt-3 flex items-start gap-2 border-t border-line pt-3 text-xs text-muted">
          <p role="status">{notice}</p>
          <button
            type="button"
            className="text-muted hover:text-body"
            aria-label="Dismiss notice"
            onClick={() => setNotice(null)}
          >
            ×
          </button>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Settings bar
// ---------------------------------------------------------------------------

/** Whole days from one YYYY-MM-DD day to another (negative when `to` is earlier). */
function daysBetween(from: string, to: string): number {
  const at = (d: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
    return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : Number.NaN;
  };
  return Math.round((at(to) - at(from)) / 86_400_000);
}

function SettingsBar({
  settings,
  today,
  canEdit,
  planId,
  activePlan,
  open,
  onToggle,
}: {
  settings: PlanningSettings;
  today: string;
  canEdit: boolean;
  /** The plan whose settings are edited; null falls back to the current-plan PUT. */
  planId: number | null;
  activePlan: PlanMeta | null;
  open: boolean;
  onToggle: () => void;
}) {
  const queryClient = useQueryClient();
  const readOnly = activePlan?.status === 'sent';
  // Raw strings so a blank field is a validation error, never a silent 0.
  const [form, setForm] = useState({
    leadTimeDays: String(settings.leadTimeDays),
    coverDays: String(settings.coverDays),
    safetyPct: String(settings.safetyPct),
    useLostDemand: settings.useLostDemand,
    roundToCasePack: settings.roundToCasePack,
    countReservedInPosition: settings.countReservedInPosition,
    demandBasis: settings.demandBasis,
    horizonDate: settings.horizonDate ?? '',
    budget: settings.budget ?? '',
    // '' means no booking; otherwise the booking id as a string (Select values).
    activeBookingId: settings.activeBookingId === null ? '' : String(settings.activeBookingId),
  });
  const [error, setError] = useState<string | null>(null);

  // The bookings to choose from, for the "Plan against a booking" selector.
  const bookings = useQuery({
    queryKey: ['bookings'],
    queryFn: () => api.get<{ bookings: BookingView[] }>('/api/bookings'),
    enabled: open,
  });

  const save = useMutation({
    mutationFn: (body: PlanningSettings) =>
      planId !== null
        ? api.patch(`/api/plans/${planId}`, { settings: body })
        : api.put<PlanningSettings>('/api/planning/settings', body),
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['purchase-plan'] }),
        queryClient.invalidateQueries({ queryKey: ['plans'] }),
      ]);
      onToggle();
    },
    onError: (caught: unknown) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the settings.'),
  });

  function submit() {
    const lead = parseSettingInt(form.leadTimeDays, PLANNING_BOUNDS.leadTimeDays);
    const cover = parseSettingInt(form.coverDays, PLANNING_BOUNDS.coverDays);
    const safety = parseSettingInt(form.safetyPct, PLANNING_BOUNDS.safetyPct);
    if (lead.kind !== 'value' || cover.kind !== 'value' || safety.kind !== 'value') {
      const bad = [lead, cover, safety].find((r) => r.kind === 'error');
      if (bad && bad.kind === 'error') setError(bad.message);
      return;
    }
    const budget = parseMoney(form.budget);
    if (budget.kind === 'error') {
      setError(`Budget: ${budget.message}`);
      return;
    }
    const seasonal = form.demandBasis === 'seasonal';
    const horizon = form.horizonDate.trim();
    if (seasonal && !horizon) {
      setError('A seasonal plan needs a horizon date.');
      return;
    }
    if (seasonal && horizon <= today) {
      setError('The horizon must be after today.');
      return;
    }
    setError(null);
    save.mutate({
      leadTimeDays: lead.value,
      coverDays: cover.value,
      safetyPct: safety.value,
      useLostDemand: form.useLostDemand,
      roundToCasePack: form.roundToCasePack,
      countReservedInPosition: form.countReservedInPosition,
      demandBasis: form.demandBasis,
      // A horizon typed on the seasonal basis is kept when switching back, so
      // it is still there next season; a blank clears it.
      horizonDate: horizon || null,
      budget: budget.kind === 'clear' ? null : budget.value,
      activeBookingId: form.activeBookingId === '' ? null : Number(form.activeBookingId),
    });
  }

  const seasonal = settings.demandBasis === 'seasonal';
  const horizonDays = settings.horizonDate ? daysBetween(today, settings.horizonDate) : null;

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        {seasonal ? (
          <span className="text-muted">
            Seasonal through{' '}
            <span className="font-medium text-strong">{day(settings.horizonDate)}</span>
            {horizonDays !== null && (
              <span className="ml-1">
                ({horizonDays} day{horizonDays === 1 ? '' : 's'}, sized on last year’s same window)
              </span>
            )}
          </span>
        ) : (
          <>
            <span className="text-muted">
              Rolling: lead time{' '}
              <span className="font-medium text-strong">{settings.leadTimeDays}d</span>
            </span>
            <span className="text-muted">
              Cover <span className="font-medium text-strong">{settings.coverDays}d</span>
            </span>
          </>
        )}
        <span className="text-muted">
          Safety <span className="font-medium text-strong">{settings.safetyPct}%</span>
        </span>
        <span className="text-muted">
          Budget{' '}
          <span className="font-medium text-strong">
            {settings.budget === null ? 'none' : money(settings.budget)}
          </span>
        </span>
        {!seasonal && (
          <span className="text-muted">
            Lost demand{' '}
            <span className="font-medium text-strong">{settings.useLostDemand ? 'on' : 'off'}</span>
          </span>
        )}
        <span className="text-muted">
          Case-pack rounding{' '}
          <span className="font-medium text-strong">{settings.roundToCasePack ? 'on' : 'off'}</span>
        </span>
        {readOnly ? (
          <span className="ml-auto text-xs text-muted">
            Sent{activePlan?.sentAt ? ` on ${day(activePlan.sentAt.slice(0, 10))}` : ''} — settings
            are locked. Duplicate this plan to change them.
          </span>
        ) : (
          canEdit && (
            <Button variant="ghost" className="ml-auto" onClick={onToggle}>
              {open ? 'Close' : 'Edit planning settings'}
            </Button>
          )
        )}
      </div>

      {open && canEdit && !readOnly && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="mt-4 grid gap-4 border-t border-line pt-4 sm:grid-cols-3"
        >
          <label className="block">
            <span className="mb-1 block text-xs text-muted">Demand basis</span>
            <Select
              value={form.demandBasis}
              onChange={(e) => setForm({ ...form, demandBasis: e.target.value as DemandBasis })}
              aria-label="Demand basis"
            >
              <option value="recent">Rolling — recent rate over lead time + cover</option>
              <option value="seasonal">Seasonal — last year’s same window, through a date</option>
            </Select>
          </label>
          {form.demandBasis === 'seasonal' ? (
            <label className="block">
              <span className="mb-1 block text-xs text-muted">Horizon date</span>
              <Input
                type="date"
                value={form.horizonDate}
                min={today}
                onChange={(e) => setForm({ ...form, horizonDate: e.target.value })}
                aria-label="Horizon date"
              />
            </label>
          ) : (
            <NumberField
              label="Cover (days)"
              value={form.coverDays}
              onChange={(v) => setForm({ ...form, coverDays: v })}
            />
          )}
          <NumberField
            label="Lead time (days)"
            value={form.leadTimeDays}
            onChange={(v) => setForm({ ...form, leadTimeDays: v })}
          />
          <NumberField
            label="Safety (%)"
            value={form.safetyPct}
            onChange={(v) => setForm({ ...form, safetyPct: v })}
          />
          <label className="block">
            <span className="mb-1 block text-xs text-muted">Budget ($, blank for none)</span>
            <Input
              inputMode="decimal"
              value={form.budget}
              placeholder="none"
              onChange={(e) => setForm({ ...form, budget: e.target.value })}
              aria-label="Budget ($)"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-muted">Plan against a booking</span>
            <Select
              value={form.activeBookingId}
              onChange={(e) => setForm({ ...form, activeBookingId: e.target.value })}
              aria-label="Active booking"
            >
              <option value="">None — plan the whole catalog</option>
              {(bookings.data?.bookings ?? []).map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.name} ({b.bookingYear})
                </option>
              ))}
            </Select>
          </label>
          {form.demandBasis === 'seasonal' && (
            <p className="text-xs text-muted sm:col-span-3">
              Seasonal sizes each SKU on the units it shipped per in-stock day over the same window
              one year earlier, scaled by its trend (the last 90 days against last year’s same 90,
              held between ×0.5 and ×2), for the days after today through the horizon. Lead time
              only dates the order-by column. A size with fewer than 14 in-stock days in last year’s
              window uses its recent rate instead (lost demand then applies as on the rolling plan).
            </p>
          )}
          <label className="flex items-center gap-2 text-sm text-body">
            <input
              type="checkbox"
              checked={form.useLostDemand}
              onChange={(e) => setForm({ ...form, useLostDemand: e.target.checked })}
              className="rounded border-line-strong"
            />
            Add lost demand while out of stock
          </label>
          <label className="flex items-center gap-2 text-sm text-body">
            <input
              type="checkbox"
              checked={form.roundToCasePack}
              onChange={(e) => setForm({ ...form, roundToCasePack: e.target.checked })}
              className="rounded border-line-strong"
            />
            Round up to case packs
          </label>
          <label className="flex items-center gap-2 text-sm text-body">
            <input
              type="checkbox"
              checked={form.countReservedInPosition}
              onChange={(e) => setForm({ ...form, countReservedInPosition: e.target.checked })}
              className="rounded border-line-strong"
            />
            Count reserved units in the position
          </label>
          <div className="flex items-center gap-3 sm:col-span-3">
            <Button type="submit" variant="secondary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save settings'}
            </Button>
            {error && <Alert>{error}</Alert>}
          </div>
        </form>
      )}
    </Card>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-muted">{label}</span>
      <Input inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Inline-editable cells (each owns its save, error, and revert)
// ---------------------------------------------------------------------------

/**
 * An inline field that parses strictly, saves on Enter or blur, shows its own
 * error beside the cell, reverts to the stored value on a failed save, and
 * resyncs when the stored value changes while it is not being edited.
 */
function PlanEditCell({
  skuId,
  field,
  initial,
  parse,
  ariaLabel,
  placeholder,
  width = 'w-16',
  afterSave,
  mutate,
}: {
  skuId: number;
  field: 'overrideQty' | 'casePack' | 'unitCost';
  initial: string;
  parse: (raw: string) => ParsedField<number | string>;
  ariaLabel: string;
  placeholder?: string;
  width?: string;
  /** Called with the committed value (or null on clear) after a successful save. */
  afterSave?: (value: number | string | null) => void;
  /**
   * A custom save (for a per-plan override); when omitted the value is PATCHed
   * onto the SKU (`field`). See FinalCell for the override save.
   */
  mutate?: (next: number | string | null) => Promise<unknown>;
}) {
  const invalidate = usePlanInvalidate();
  const [value, setValue] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const focused = useRef(false);
  // Set by Escape before blur(): commit() sees it and abandons the typed value
  // instead of saving it (blur fires synchronously, before setValue applies).
  const cancelled = useRef(false);

  // Resync to the stored value when it changes and we are not mid-edit.
  useEffect(() => {
    if (!focused.current) {
      setValue(initial);
      setError(null);
    }
  }, [initial]);

  const save = useMutation({
    mutationFn: (next: number | string | null) =>
      mutate ? mutate(next) : api.patch(`/api/skus/${skuId}`, { [field]: next }),
    onSuccess: () => invalidate(),
    onError: (caught: unknown) => {
      setError(caught instanceof ApiError ? caught.message : 'Could not save.');
      setValue(initial); // revert to the stored value on failure
    },
  });

  function commit() {
    if (cancelled.current) {
      cancelled.current = false;
      setValue(initial);
      setError(null);
      return;
    }
    if (value.trim() === initial.trim()) {
      setError(null);
      return;
    }
    const parsed = parse(value);
    if (parsed.kind === 'error') {
      setError(parsed.message);
      return;
    }
    setError(null);
    const next = parsed.kind === 'clear' ? null : parsed.value;
    save.mutate(next);
    afterSave?.(next);
  }

  return (
    <span className="inline-flex flex-col items-end">
      <input
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        onClick={(e) => e.stopPropagation()}
        onFocus={() => {
          focused.current = true;
        }}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          focused.current = false;
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            cancelled.current = true;
            (e.target as HTMLInputElement).blur();
          }
        }}
        className={`${width} rounded border-0 bg-surface px-1.5 py-1 text-right text-xs text-strong ring-1 ring-inset ${
          error ? 'ring-danger-line' : 'ring-line-strong'
        } focus:ring-2 focus:ring-inset focus:ring-brand-600`}
      />
      {error && (
        <span className="mt-0.5 text-[10px] text-danger-text" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

/** A one-click PATCH (clear an override, toggle a flag) with its own error line. */
function InstantEdit({
  skuId,
  body,
  mutate,
  label,
  title,
  className = '',
  onDone,
  children,
}: {
  skuId: number;
  body?: Record<string, unknown>;
  /** A custom save (for a per-plan override); when omitted PATCHes `body` onto the SKU. */
  mutate?: () => Promise<unknown>;
  label: string;
  title?: string;
  className?: string;
  onDone?: () => void;
  children: ReactNode;
}) {
  const invalidate = usePlanInvalidate();
  const save = useMutation({
    mutationFn: () => (mutate ? mutate() : api.patch(`/api/skus/${skuId}`, body ?? {})),
    onSuccess: () => {
      onDone?.();
      return invalidate();
    },
  });
  return (
    <button
      type="button"
      aria-label={label}
      title={save.isError ? 'Could not save — try again.' : title}
      disabled={save.isPending}
      onClick={(e) => {
        e.stopPropagation();
        save.mutate();
      }}
      className={`${className} ${save.isError ? 'text-danger-text' : ''}`}
    >
      {children}
    </button>
  );
}

/** A small popover to edit the override note (fills the agent CSV's note column). */
function NotePopover({
  skuId,
  planId,
  note,
}: {
  skuId: number;
  planId: number;
  note: string | null;
}) {
  const invalidate = usePlanInvalidate();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(note ?? '');
  useEffect(() => setValue(note ?? ''), [note]);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/plans/${planId}/overrides`, { skuId, note: value.trim() || null }),
    onSuccess: async () => {
      await invalidate();
      setOpen(false);
    },
  });

  return (
    <span className="relative">
      <button
        type="button"
        aria-label="Edit override note"
        title={note ? `Note: ${note}` : 'Add a note'}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={note ? 'text-brand-600' : 'text-muted hover:text-body'}
      >
        {note ? '✎' : '＋'}
      </button>
      {open && (
        <div
          className="absolute right-0 z-30 mt-1 w-56 rounded-md border border-line bg-surface p-2 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={3}
            aria-label="Override note"
            placeholder="Why this quantity (shown to the agent)"
            className="w-full rounded border-0 bg-surface p-1.5 text-xs text-strong ring-1 ring-inset ring-line-strong focus:ring-2 focus:ring-inset focus:ring-brand-600"
          />
          <div className="mt-1 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Close
            </Button>
            <Button variant="secondary" disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
          {save.isError && <p className="mt-1 text-[10px] text-danger-text">Could not save.</p>}
        </div>
      )}
    </span>
  );
}

function ConfidenceBadge({ row }: { row: PlanRow }) {
  return (
    <span
      className={`inline-flex cursor-help items-center rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${CONFIDENCE_CLASS[row.confidence]}`}
      title={row.reasons.join('\n') || 'No notes.'}
    >
      {CONFIDENCE_LABEL[row.confidence]}
    </span>
  );
}

/** The Final-quantity cell: the override editor, its clear and note, or a suppressed override. */
function FinalCell({
  row,
  canEdit,
  planId,
  onOverride,
}: {
  row: PlanRow;
  canEdit: boolean;
  /** The plan whose override this cell edits; null (or read-only) makes it static. */
  planId: number | null;
  onOverride: (id: number, qty: number | string | null) => void;
}) {
  if (row.reorderExcluded || row.delistedOn !== null) {
    // Exclusion wins: the final is 0, and any stored override is shown struck
    // through with the reason it is ignored. A listing deleted on Amazon (no
    // longer in a full listings import) is excluded the same way.
    return (
      <span className="inline-flex items-center gap-1">
        <span className="font-medium text-strong">0</span>
        {row.overrideQty !== null && (
          <s
            className="text-muted"
            title={`An override of ${row.overrideQty} is stored but ignored while this SKU is excluded.`}
          >
            {integer(row.overrideQty)}
          </s>
        )}
      </span>
    );
  }
  if (!canEdit || planId === null) {
    return (
      <span className={row.overrideQty !== null ? 'font-medium text-strong' : ''}>
        {integer(row.finalQty)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <PlanEditCell
        skuId={row.id}
        field="overrideQty"
        initial={row.overrideQty === null ? '' : String(row.overrideQty)}
        placeholder={String(row.suggestedQty)}
        parse={(raw) => parseCount(raw, { min: 0 })}
        ariaLabel={`Override quantity for ${row.sellerSku}`}
        mutate={(next) => api.put(`/api/plans/${planId}/overrides`, { skuId: row.id, qty: next })}
        afterSave={(v) => onOverride(row.id, v)}
      />
      {row.overrideQty !== null && (
        <InstantEdit
          skuId={row.id}
          mutate={() => api.put(`/api/plans/${planId}/overrides`, { skuId: row.id, qty: null })}
          label={`Clear override for ${row.sellerSku}`}
          title="Clear override"
          className="text-muted hover:text-danger-text"
          onDone={() => onOverride(row.id, null)}
        >
          ×
        </InstantEdit>
      )}
      <NotePopover skuId={row.id} planId={planId} note={row.overrideNote} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

const skuHelper = createColumnHelper<PlanRow>();

/** The budget cell: the funded quantity and its verdict, with the rank and cost on hover. */
function BudgetCell({ row }: { row: PlanRow }) {
  const line = row.budget;
  if (!line) return <span className="text-muted">—</span>;
  const title =
    line.status === 'uncosted'
      ? 'No unit cost, so this line could not be allocated; it keeps its final quantity.'
      : line.status === 'held'
        ? `#${line.rank} in the funding order · low confidence with no override, so the agent list leaves it out and the budget spends nothing on it. Give it an override to fund it.`
        : `#${line.rank} in the funding order · ${money(line.cost)}${
            line.status === 'partial' ? ` · ${integer(row.finalQty - line.qty)} units unfunded` : ''
          }`;
  return (
    <span className="inline-flex items-center gap-1.5" title={title}>
      <span className="font-medium text-strong">{integer(line.qty)}</span>
      <span
        className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${BUDGET_STATUS_CLASS[line.status]}`}
      >
        {BUDGET_STATUS_LABEL[line.status]}
      </span>
    </span>
  );
}

function buildSkuColumns(
  {
    canEdit,
    seasonal,
    hasBudget,
    bookingActive,
    activeBookingId,
    planId,
    readOnly,
    leadBySku,
  }: {
    canEdit: boolean;
    seasonal: boolean;
    hasBudget: boolean;
    bookingActive: boolean;
    activeBookingId: number | null;
    /** The plan being edited; overrides save against it. Null before any plan. */
    planId: number | null;
    /** A sent plan is frozen: every inline edit is disabled and the value is static. */
    readOnly: boolean;
    /** Observed lead times per seller SKU, for the "Obs. lead" column and the inbound cross-check. */
    leadBySku: Map<string, SkuLeadTime>;
  },
  openSku: (id: number) => void,
  onOverride: (id: number, qty: number | string | null) => void,
) {
  // A sent plan is read-only, whatever the role: no inline edits.
  const editable = canEdit && !readOnly;
  // Booking-only columns: the line's shipment window and its flags, the MAP, and
  // the order net profit — shown only when a booking is active.
  const bookingColumns = bookingActive
    ? [
        skuHelper.accessor((r) => r.booking?.window ?? undefined, {
          id: 'bookingWindow',
          header: () => abbr('Window', 'Booking shipment window; pre-book and launch flagged'),
          sortingFn: sortingFnFor(compareText),
          cell: (c) => {
            const b = c.row.original.booking;
            if (!b) return <span className="text-muted">—</span>;
            return (
              <span className="whitespace-nowrap">
                {WINDOW_LABELS[b.window] ?? b.window}
                {b.prebook && <span className="ml-1 text-warn-text">pre-book</span>}
                {!b.prebook && b.cannotArriveByHorizon && (
                  <span
                    className="ml-1 text-warn-text"
                    title="May not arrive before the seasonal horizon (pre-book or a launch date after it)."
                  >
                    late
                  </span>
                )}
                {b.launchDate && (
                  <span className="ml-1 text-xs text-muted" title={`Launches ${b.launchDate}`}>
                    launch
                  </span>
                )}
                {b.packQty > 1 && (
                  <span
                    className="ml-1 text-xs text-warn-text"
                    title={`Priced per pack of ${b.packQty}. The plan writes the SKU’s own unit count into a pack-priced cell — check this line by hand.`}
                  >
                    pack of {b.packQty}
                  </span>
                )}
                {b.pair?.preferRacquetFallback && (
                  <span
                    className="ml-1 text-xs text-warn-text"
                    title="This pair is set to order from the racquet tab, but the racquet has no orderable line — the demo is ordered instead."
                  >
                    racquet not orderable, demo ordered
                  </span>
                )}
                {editable && b.pair && activeBookingId !== null && (
                  <PreferRacquetToggle bookingId={activeBookingId} pair={b.pair} />
                )}
              </span>
            );
          },
        }),
        skuHelper.accessor((r) => r.booking?.map ?? undefined, {
          id: 'bookingMap',
          header: () =>
            abbr('MAP', 'Minimum advertised price from the booking (per pack when noted)'),
          meta: { numeric: true },
          sortingFn: sortingFnFor(compareNumericString),
          cell: (c) => {
            const b = c.row.original.booking;
            if (!b || b.map === null) return <span className="text-muted">—</span>;
            return (
              <span
                title={
                  b.mapPackQty && b.mapPackQty > 1 ? `MAP per pack of ${b.mapPackQty}` : undefined
                }
              >
                {money(b.map)}
                {b.mapPackQty && b.mapPackQty > 1 ? (
                  <span className="ml-0.5 text-xs text-muted">/{b.mapPackQty}</span>
                ) : null}
              </span>
            );
          },
        }),
        skuHelper.accessor((r) => r.orderProfit ?? undefined, {
          id: 'orderProfit',
          header: () => abbr('Net', 'Order net profit: total MAP minus cost, for what is ordered'),
          meta: { numeric: true },
          sortingFn: sortingFnFor(compareNumericString),
          cell: (c) => money(c.row.original.orderProfit),
        }),
      ]
    : [];
  // Seasonal-only columns: last year's window and the trend, beside the rate used.
  const seasonalColumns = seasonal
    ? [
        skuHelper.accessor((r) => r.seasonal?.lastYearPer30 ?? undefined, {
          id: 'lastYearPer30',
          header: () =>
            abbr('Last yr/30', 'Units per 30 in-stock days over the same window last year'),
          meta: { numeric: true },
          sortingFn: sortingFnFor(compareNumericString),
          cell: (c) => {
            const s = c.row.original.seasonal;
            if (!s) return '—';
            return (
              <span
                title={`${integer(s.lastYearUnits)} units over ${integer(s.lastYearInStockDays)} in-stock days (${day(s.windowFrom)} – ${day(s.windowTo)})`}
              >
                {decimal(s.lastYearPer30, 1)}{' '}
                <span className="text-xs text-muted">{integer(s.lastYearInStockDays)}d</span>
              </span>
            );
          },
        }),
        skuHelper.accessor((r) => r.seasonal?.trendFactor ?? undefined, {
          id: 'trendFactor',
          header: () =>
            abbr('Trend', 'This year’s last 90 days against last year’s same 90 (×0.5 to ×2)'),
          meta: { numeric: true },
          sortingFn: sortingFnFor(compareNumericString),
          cell: (c) => {
            const s = c.row.original.seasonal;
            if (!s || s.trendFactor === null) return <span className="text-muted">—</span>;
            return <span title={s.trendClamped ? 'Clamped' : undefined}>×{s.trendFactor}</span>;
          },
        }),
        skuHelper.accessor('recentPer30', {
          header: () => abbr('Recent/30', 'The recent rate the rolling plan would use'),
          meta: { numeric: true },
          sortingFn: sortingFnFor(compareNumericString),
          cell: (c) => decimal(c.getValue(), 1),
        }),
      ]
    : [];

  return [
    skuHelper.accessor('sellerSku', {
      header: 'SKU',
      sortingFn: sortingFnFor(compareText),
      cell: (c) => {
        const row = c.row.original;
        // A frozen line whose SKU was later deleted carries a negative synthetic
        // id (there is no SKU page to open): show it as plain text, not a link.
        const label =
          row.id <= 0 ? (
            <span className="font-medium text-strong">{c.getValue()}</span>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openSku(row.id);
              }}
              className="font-medium text-brand-600 hover:underline"
            >
              {c.getValue()}
            </button>
          );
        return (
          <span className="flex items-center gap-2">
            <ProductThumb asin={row.asin} title={row.title} />
            <span className="whitespace-nowrap">{label}</span>
          </span>
        );
      },
    }),
    // Product before Variant: SKU, Product, Variant is the owner's reading order.
    skuHelper.accessor((r) => r.family?.name ?? undefined, {
      id: 'family',
      header: 'Product',
      sortingFn: sortingFnFor(compareText),
      cell: (c) => {
        const row = c.row.original;
        const family = row.family;
        // The family link, then the product page (the child ASIN, every SKU on it).
        return (
          <span className="flex items-center gap-2">
            {family ? (
              <Link
                to={familyPath(family.key)}
                onClick={(e) => e.stopPropagation()}
                className="block max-w-[10rem] truncate text-brand-600 hover:underline"
                title={family.name || family.key}
              >
                {family.name || family.key}
              </Link>
            ) : (
              '—'
            )}
            <ProductPageLink asin={row.asin} skuId={row.id} />
          </span>
        );
      },
    }),
    skuHelper.accessor((r) => r.variantLabel || undefined, {
      id: 'variant',
      header: () => abbr('Variant', 'Grip or shoe size, parsed from the title (a heuristic)'),
      sortingFn: sortingFnFor(compareText),
      cell: (c) => c.getValue() ?? '—',
    }),
    skuHelper.accessor((r) => r.stock ?? undefined, {
      id: 'stock',
      header: () => abbr('Stock', 'On hand at Amazon (unknown or negative counts as 0)'),
      meta: { numeric: true },
      sortingFn: sortingFnFor(compareInteger),
      cell: (c) =>
        c.row.original.stockUnknown ? (
          <span className="text-amber-600 dark:text-amber-400" title="Unknown or negative on-hand">
            ?
          </span>
        ) : (
          integer(c.getValue() ?? null)
        ),
    }),
    skuHelper.accessor('inbound', {
      header: () => abbr('Inb.', 'Inbound units from the latest FBA census (what the plan counts)'),
      meta: { numeric: true },
      sortingFn: sortingFnFor(compareInteger),
      cell: (c) => {
        // The census figure is what the position counts. The shipment-derived open
        // inbound is a cross-check only, shown in the tooltip — never added to the
        // arithmetic (see docs/lead-times.md).
        const open = leadBySku.get(c.row.original.sellerSku)?.openInboundUnits ?? 0;
        return (
          <span
            title={
              open > 0
                ? `Cross-check: ${open} units shipped-minus-ledger-received on non-terminal FBA shipments (cross-check only; the census figure is what the plan counts).`
                : undefined
            }
          >
            {integer(c.getValue())}
            {open > 0 && <span className="ml-1 text-xs text-muted">({open}?)</span>}
          </span>
        );
      },
    }),
    skuHelper.accessor('reserved', {
      header: () => abbr('Rsvd', 'Reserved units (counted in the position when the setting is on)'),
      meta: { numeric: true },
      sortingFn: sortingFnFor(compareInteger),
      cell: (c) => integer(c.getValue()),
    }),
    ...seasonalColumns,
    ...bookingColumns,
    skuHelper.accessor('velocityPer30', {
      header: () =>
        abbr(
          seasonal ? 'Rate/30' : 'Vel/30',
          seasonal
            ? 'The rate that sized this line: last year × trend, or the recent rate when last year has no in-stock history'
            : 'Units per 30 days at the daily velocity',
        ),
      meta: { numeric: true },
      sortingFn: sortingFnFor(compareNumericString),
      cell: (c) => (
        <span title={VELOCITY_SOURCE_FULL[c.row.original.velocitySource]}>
          {decimal(c.getValue(), 1)}{' '}
          <span className="text-xs text-muted">
            {VELOCITY_SOURCE_LABEL[c.row.original.velocitySource]}
          </span>
        </span>
      ),
    }),
    // Lost demand, the projected stockout, and the order-by date belong to the
    // rolling plan; a seasonal order is sized to a horizon instead.
    ...(seasonal
      ? []
      : [
          skuHelper.accessor('lostPer30', {
            header: () =>
              abbr(
                'Lost/30',
                'Estimated lost units per 30 days while out of stock (monthly-rate SKUs)',
              ),
            meta: { numeric: true },
            sortingFn: sortingFnFor(compareNumericString),
            cell: (c) => decimal(c.getValue(), 1),
          }),
        ]),
    skuHelper.accessor((r) => r.daysOfCoverNow ?? undefined, {
      id: 'daysOfCoverNow',
      header: () => abbr('Cover', 'Days of cover on hand plus inbound, at the rate used'),
      meta: { numeric: true },
      sortingFn: sortingFnFor(compareNumericString),
      cell: (c) => decimal(c.row.original.daysOfCoverNow, 0),
    }),
    ...(seasonal
      ? []
      : [
          skuHelper.accessor((r) => r.projectedStockoutDate ?? undefined, {
            id: 'projectedStockoutDate',
            header: () => abbr('Stockout', 'Projected stockout date'),
            sortingFn: sortingFnFor(compareIsoDate),
            cell: (c) => day(c.getValue()),
          }),
          skuHelper.accessor((r) => r.orderByDate ?? undefined, {
            id: 'orderByDate',
            header: () => abbr('Order by', 'Last day to place the order'),
            sortingFn: sortingFnFor(compareIsoDate),
            cell: (c) =>
              c.getValue() === undefined ? (
                '—'
              ) : (
                <span className={c.row.original.late ? 'font-medium text-danger-text' : ''}>
                  {day(c.getValue())}
                </span>
              ),
          }),
        ]),
    skuHelper.accessor((r) => leadBySku.get(r.sellerSku)?.suggestedLeadTimeDays ?? undefined, {
      id: 'observedLead',
      header: () =>
        abbr('Obs. lead', 'Observed lead time (p80 of total days) with the matched-order count'),
      meta: { numeric: true },
      sortingFn: sortingFnFor(compareInteger),
      cell: (c) => {
        const stat = leadBySku.get(c.row.original.sellerSku);
        if (!stat || stat.n === 0 || stat.suggestedLeadTimeDays === null) {
          return <span className="text-muted">—</span>;
        }
        return (
          <span
            title={`Median ${stat.medianTotal ?? '—'}d, p80 ${stat.p80Total ?? '—'}d over ${stat.n} matched ${stat.n === 1 ? 'order' : 'orders'}. See the SKU page for the detail.`}
          >
            {integer(stat.suggestedLeadTimeDays)}d{' '}
            <span className="text-xs text-muted">n{stat.n}</span>
          </span>
        );
      },
    }),
    skuHelper.accessor('suggestedQty', {
      header: () => abbr('Sugg.', 'Suggested order quantity'),
      meta: { numeric: true },
      sortingFn: sortingFnFor(compareInteger),
      cell: (c) => integer(c.getValue()),
    }),
    skuHelper.accessor('finalQty', {
      header: () => abbr('Final', 'Final order quantity (override or suggestion)'),
      meta: { numeric: true },
      sortingFn: sortingFnFor(compareInteger),
      cell: (c) => (
        <FinalCell
          row={c.row.original}
          canEdit={editable}
          planId={editable ? planId : null}
          onOverride={onOverride}
        />
      ),
    }),
    ...(hasBudget
      ? [
          skuHelper.accessor((r) => r.budget?.qty ?? undefined, {
            id: 'budgetQty',
            header: () => abbr('Budget', 'What the budget funds on this line, and its verdict'),
            meta: { numeric: true },
            sortingFn: sortingFnFor(compareInteger),
            cell: (c) => <BudgetCell row={c.row.original} />,
          }),
        ]
      : []),
    skuHelper.accessor((r) => r.casePack ?? undefined, {
      id: 'casePack',
      header: () => abbr('Case', 'Case pack'),
      meta: { numeric: true },
      sortingFn: sortingFnFor(compareInteger),
      cell: (c) => {
        const row = c.row.original;
        if (!editable) return integer(row.casePack);
        return (
          <PlanEditCell
            skuId={row.id}
            field="casePack"
            initial={row.casePack === null ? '' : String(row.casePack)}
            placeholder="1"
            width="w-12"
            parse={(raw) => parseCount(raw, { min: 1 })}
            ariaLabel={`Case pack for ${row.sellerSku}`}
          />
        );
      },
    }),
    skuHelper.accessor((r) => r.unitCost ?? undefined, {
      id: 'unitCost',
      header: () =>
        abbr(
          'Cost',
          bookingActive ? 'The booking line’s discounted cost' : 'Owner-entered landed unit cost',
        ),
      meta: { numeric: true },
      sortingFn: sortingFnFor(compareNumericString),
      cell: (c) => {
        const row = c.row.original;
        // With a booking, the line's discounted cost drives the plan and is not
        // the editable SKU field: show it read-only with its price label.
        if (row.booking) {
          // A line may quote no discounted cost; then the plan falls back to the
          // SKU's own cost. Label the source so a fallback is never read as a
          // booking price.
          const fallback = row.booking.costSource === 'sku_fallback';
          return (
            <span
              title={
                fallback
                  ? 'The booking line quotes no cost; using the SKU’s own unit cost.'
                  : (row.booking.priceLabel ?? 'Booking price')
              }
              className="whitespace-nowrap"
            >
              {money(row.unitCost)}
              <span className="ml-1 text-xs text-muted">
                {fallback ? 'SKU cost' : (row.booking.priceLabel ?? 'booking')}
              </span>
            </span>
          );
        }
        if (!editable) return money(row.skuUnitCost);
        return (
          <PlanEditCell
            skuId={row.id}
            field="unitCost"
            initial={row.skuUnitCost ?? ''}
            placeholder="0.00"
            parse={parseMoney}
            ariaLabel={`Unit cost for ${row.sellerSku}`}
          />
        );
      },
    }),
    skuHelper.accessor((r) => r.extendedCost ?? undefined, {
      id: 'extendedCost',
      header: () => abbr('Ext.', 'Final quantity × unit cost'),
      meta: { numeric: true },
      sortingFn: sortingFnFor(compareNumericString),
      cell: (c) => money(c.row.original.extendedCost),
    }),
    skuHelper.accessor('confidence', {
      header: 'Conf.',
      sortingFn: sortingFnFor((a, b) =>
        a === undefined || b === undefined
          ? 0
          : WORST_CONFIDENCE_ORDER[a as Confidence] - WORST_CONFIDENCE_ORDER[b as Confidence],
      ),
      cell: (c) => <ConfidenceBadge row={c.row.original} />,
    }),
    skuHelper.display({
      id: 'reorder',
      header: () => abbr('No reorder', 'Mark as do-not-reorder (discontinued)'),
      cell: (c) => {
        const row = c.row.original;
        return (
          <ReorderToggle
            skuId={row.id}
            sku={row.sellerSku}
            excluded={row.reorderExcluded}
            disabled={!editable}
          />
        );
      },
    }),
  ];
}

/** The do-not-reorder checkbox: its own mutation, controlled by the plan data. */
function ReorderToggle({
  skuId,
  sku,
  excluded,
  disabled,
}: {
  skuId: number;
  sku: string;
  excluded: boolean;
  disabled: boolean;
}) {
  const invalidate = usePlanInvalidate();
  const save = useMutation({
    mutationFn: (next: boolean) => api.patch(`/api/skus/${skuId}`, { reorderExcluded: next }),
    onSuccess: () => invalidate(),
  });
  return (
    <input
      type="checkbox"
      checked={excluded}
      disabled={disabled || save.isPending}
      aria-label={`Do not reorder ${sku}`}
      title={save.isError ? 'Could not save — try again.' : undefined}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => save.mutate(e.target.checked)}
      className="rounded border-line-strong"
    />
  );
}

/**
 * The per-pair "order from the racquet tab" lever, shown on a clean racquet/demo
 * pair line. Checked orders the pair on the racquet cell/cost; unchecked (null)
 * leaves the demo preferred when orderable. Stored on the demo twin's key.
 */
function PreferRacquetToggle({ bookingId, pair }: { bookingId: number; pair: BookingPairLever }) {
  const invalidate = usePlanInvalidate();
  const save = useMutation({
    mutationFn: (next: boolean) =>
      api.post(`/api/bookings/${bookingId}/prefer-racquet`, {
        itemCode: pair.demoItemCode,
        sizeLabel: pair.demoSizeLabel === '' ? null : pair.demoSizeLabel,
        discriminator: pair.demoDiscriminator,
        preferRacquet: next ? true : null,
      }),
    onSuccess: () => invalidate(),
  });
  return (
    <label
      className="ml-1 inline-flex items-center gap-1 text-xs text-muted"
      title={
        save.isError
          ? 'Could not save — try again.'
          : 'Order this racquet/demo pair from the racquet tab (its cell and cost), not the demo. Marking the demo “no listing” alone does not change which cell is ordered when the racquet is confirmed — this is the lever.'
      }
    >
      <input
        type="checkbox"
        checked={pair.preferRacquet === true}
        disabled={save.isPending}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => save.mutate(e.target.checked)}
        className="rounded border-line-strong"
      />
      racquet tab
    </label>
  );
}

const familyHelper = createColumnHelper<FamilyPlanRow>();

/**
 * The product roll-up columns; with a budget, what it funds per product. The
 * order-by date is a rolling-plan figure, hidden on the seasonal basis like
 * the SKU tab's.
 */
function buildFamilyColumns({ hasBudget, seasonal }: { hasBudget: boolean; seasonal: boolean }) {
  const budgetColumns = hasBudget
    ? [
        familyHelper.accessor((r) => r.orderQty - r.uncostedOrderUnits, {
          id: 'fundedQty',
          header: () => abbr('Funded', 'Units the budget funds across the product'),
          meta: { numeric: true },
          sortingFn: sortingFnFor(compareInteger),
          cell: (c) => {
            const counts = c.row.original.budgetStatusCounts;
            const note = [
              counts.cut > 0 ? `${counts.cut} cut` : '',
              counts.partial > 0 ? `${counts.partial} partial` : '',
              counts.held > 0 ? `${counts.held} to review` : '',
              counts.uncosted > 0 ? `${counts.uncosted} uncosted` : '',
            ]
              .filter(Boolean)
              .join(', ');
            return (
              <span title={note || undefined}>
                <span className="font-medium text-strong">{integer(c.getValue())}</span>
                {note && <span className="ml-1 text-xs text-warn-text">{note}</span>}
              </span>
            );
          },
        }),
        familyHelper.accessor((r) => r.budgetCost ?? undefined, {
          id: 'budgetCost',
          header: () => abbr('Funded $', 'Cost of what the budget funds across the product'),
          meta: { numeric: true },
          sortingFn: sortingFnFor(compareNumericString),
          cell: (c) => money(c.row.original.budgetCost),
        }),
      ]
    : [];
  // FAMILY_BASE_COLUMNS ends with the order-by date and the confidence badge.
  const tail = seasonal ? FAMILY_BASE_COLUMNS.slice(9) : FAMILY_BASE_COLUMNS.slice(8);
  return [...FAMILY_BASE_COLUMNS.slice(0, 8), ...budgetColumns, ...tail];
}

const FAMILY_BASE_COLUMNS = [
  familyHelper.accessor('name', {
    header: 'Product',
    sortingFn: sortingFnFor(compareText),
    cell: (c) => (
      <span className="block max-w-[16rem] truncate font-medium text-strong" title={c.getValue()}>
        {c.getValue()}
      </span>
    ),
  }),
  familyHelper.accessor('childCount', {
    header: () => abbr('SKUs', 'Child SKUs in the family'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  familyHelper.accessor('childrenToOrder', {
    header: () => abbr('To order', 'Child SKUs with a quantity to order'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  familyHelper.accessor((r) => r.stock ?? undefined, {
    id: 'stock',
    header: () => abbr('Stock', 'On hand across the family'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue() ?? null),
  }),
  familyHelper.accessor('units365', {
    header: () => abbr('U365', 'Units sold, last 365 days'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  familyHelper.accessor('suggestedQty', {
    header: () => abbr('Sugg.', 'Suggested units to order'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => integer(c.getValue()),
  }),
  familyHelper.accessor('finalQty', {
    header: () => abbr('Final', 'Final units to order'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareInteger),
    cell: (c) => <span className="font-medium text-strong">{integer(c.getValue())}</span>,
  }),
  familyHelper.accessor((r) => r.extendedCost ?? undefined, {
    id: 'extendedCost',
    header: () => abbr('Ext.', 'Extended cost of the family order'),
    meta: { numeric: true },
    sortingFn: sortingFnFor(compareNumericString),
    cell: (c) => money(c.row.original.extendedCost),
  }),
  familyHelper.accessor((r) => r.earliestOrderByDate ?? undefined, {
    id: 'earliestOrderByDate',
    header: () => abbr('Order by', 'Earliest order-by date across children'),
    sortingFn: sortingFnFor(compareIsoDate),
    cell: (c) =>
      c.getValue() === undefined ? (
        '—'
      ) : (
        <span className={c.row.original.late ? 'font-medium text-danger-text' : ''}>
          {day(c.getValue())}
        </span>
      ),
  }),
  familyHelper.accessor((r) => r.worstConfidence ?? undefined, {
    id: 'worstConfidence',
    header: 'Conf.',
    sortingFn: sortingFnFor((a, b) =>
      a === undefined || b === undefined
        ? 0
        : WORST_CONFIDENCE_ORDER[a as Confidence] - WORST_CONFIDENCE_ORDER[b as Confidence],
    ),
    cell: (c) => {
      const conf = c.row.original.worstConfidence;
      if (conf === null) return <span className="text-muted">—</span>;
      return (
        <span
          className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${CONFIDENCE_CLASS[conf]}`}
        >
          {CONFIDENCE_LABEL[conf]}
        </span>
      );
    },
  }),
];
